/*
 * The colours around and inside one finding rectangle, read off the page canvas pdf.js painted.
 *
 * The anonymized export deletes a finding's glyphs and leaves everything behind them alone, so a
 * shaded table cell stays shaded; it sets the substitute in the colour of the text it replaces. The
 * preview cannot delete glyphs from a picture, so it covers them in the colour of the paper around
 * them and writes the substitute in the colour the glyphs were painted in.
 */

/** The two colours of a rectangle, as `#rrggbb`. */
export interface PaperAndInk {
  /** The paper right around the glyph box, to cover the original glyphs with. */
  paper: string;
  /** The glyphs themselves, to set the substitute in. */
  ink: string;
}

/** A rectangle in canvas (device) pixels. */
export interface DeviceBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** What the page shows when nothing could be read: black text on white paper. */
export const DEFAULT_COLORS: PaperAndInk = { paper: '#ffffff', ink: '#000000' };

/** Width of the ring around the glyph box read for a box too small to hold a pixel, in device pixels. */
const RING = 3;

/**
 * Minimum RGB distance (of the 441 possible) between a pixel and the paper for it to be ink. Below
 * it the "darkest" pixel is antialiasing noise or a box without glyphs.
 */
const MIN_INK_DISTANCE = 64;

/** Paper pixels are grouped by their top four bits per channel, so antialiasing does not split a colour. */
const bucketOf = (r: number, g: number, b: number): number => ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);

const channelHex = (value: number): string => Math.round(value).toString(16).padStart(2, '0');

const toHex = (r: number, g: number, b: number): string => `#${channelHex(r)}${channelHex(g)}${channelHex(b)}`;

/**
 * Read the paper and ink colours of one glyph box off the canvas.
 *
 * The paper is the most common colour inside the box: antialiased text spreads over many shades,
 * so the background is the commonest one even where text covers much of the box. Inside, not
 * around: a table header sets white text on a dark fill that ends at the box's edge, and the white
 * page around it would make the fill look like ink. The thin ring around the box stands in only
 * for a box too small to hold a pixel. The ink is the pixel inside the box furthest from that
 * paper, which for antialiased text is the glyph's own colour, light on dark as well as dark on
 * light. A canvas that cannot be read (tainted, lost) gives the defaults rather than an error.
 */
export const readPaperAndInk = (
  context: Pick<CanvasRenderingContext2D, 'getImageData'>,
  box: DeviceBox,
  canvasWidth: number,
  canvasHeight: number
): PaperAndInk => {
  const boxLeft = Math.round(box.left);
  const boxTop = Math.round(box.top);
  const boxRight = Math.round(box.left + box.width);
  const boxBottom = Math.round(box.top + box.height);
  const left = Math.max(boxLeft - RING, 0);
  const top = Math.max(boxTop - RING, 0);
  const right = Math.min(boxRight + RING, canvasWidth);
  const bottom = Math.min(boxBottom + RING, canvasHeight);
  if (right <= left || bottom <= top) return DEFAULT_COLORS;

  let data: Uint8ClampedArray;
  try {
    data = context.getImageData(left, top, right - left, bottom - top).data;
  } catch {
    return DEFAULT_COLORS;
  }

  const width = right - left;
  type Bucket = { count: number; r: number; g: number; b: number };
  const add = (buckets: Map<number, Bucket>, offset: number) => {
    const r = data[offset];
    const g = data[offset + 1];
    const b = data[offset + 2];
    const key = bucketOf(r, g, b);
    const bucket = buckets.get(key) ?? { count: 0, r: 0, g: 0, b: 0 };
    bucket.count += 1;
    bucket.r += r;
    bucket.g += g;
    bucket.b += b;
    buckets.set(key, bucket);
  };
  const insideBuckets = new Map<number, Bucket>();
  const ringBuckets = new Map<number, Bucket>();
  const inside: number[] = [];
  for (let y = top; y < bottom; y += 1) {
    for (let x = left; x < right; x += 1) {
      const offset = ((y - top) * width + (x - left)) * 4;
      if (x >= boxLeft && x < boxRight && y >= boxTop && y < boxBottom) {
        inside.push(offset);
        add(insideBuckets, offset);
      } else {
        add(ringBuckets, offset);
      }
    }
  }

  let paper = { r: 255, g: 255, b: 255 };
  let most = 0;
  for (const bucket of (insideBuckets.size > 0 ? insideBuckets : ringBuckets).values()) {
    if (bucket.count > most) {
      most = bucket.count;
      paper = { r: bucket.r / bucket.count, g: bucket.g / bucket.count, b: bucket.b / bucket.count };
    }
  }

  let ink = DEFAULT_COLORS.ink;
  let furthest = MIN_INK_DISTANCE;
  for (const offset of inside) {
    const r = data[offset];
    const g = data[offset + 1];
    const b = data[offset + 2];
    const distance = Math.hypot(r - paper.r, g - paper.g, b - paper.b);
    if (distance > furthest) {
      furthest = distance;
      ink = toHex(r, g, b);
    }
  }

  return { paper: toHex(paper.r, paper.g, paper.b), ink };
};
